import React, { useImperativeHandle, useRef } from "react";
import { Grid } from "@mui/material";
import {
	moreSheet,
	contactSheet,
	rowLabelCheckboxSheet,
	SpecialConditionBox,
	getMemberSheet,
	PrivacyDataToggle,
} from "@icoach/components/";
import { Button, DateIntervalField, FormControl, FormLabel, TextField, Tooltip, Typography } from "@curves/ui-base";
import { getToday, getFirstOfMonth } from "@util/moment";
import { refIsRequiredError } from "@util/utils";

const CrossClubMinTimesField = React.forwardRef((props, ref) => {
	const { label = "", paramsData } = props;
	const minTimesRef = useRef(null);

	useImperativeHandle(
		ref,
		() => {
			return {
				isError: () => {
					const minTimes = minTimesRef.current.getResult() * 1;
					const error = minTimes < 1;
					minTimesRef.current.setError(error, error ? "請輸入1以上的數值" : "");
					return error;
				},
				getResult: () => {
					return {
						minTimes: minTimesRef.current.getResult() * 1,
					};
				},
			};
		},
		[]
	);

	return (
		<FormControl direction="column">
			{label && <FormLabel lightColor={false}>{label}</FormLabel>}
			<TextField ref={minTimesRef} defaultValue={paramsData.minTimes || 1} maskType="MONEY" fullWidth />
		</FormControl>
	);
});

//********************** 跨店名單 **********************//
export const FilterCrossClubSearch = React.forwardRef(({ paramsData }, ref) => {
	const dateRangeRef = useRef(null);
	const minTimesRef = useRef(null);

	useImperativeHandle(
		ref,
		() => {
			return {
				isError: () => refIsRequiredError(dateRangeRef, minTimesRef),
				getResult: () => {
					const dateRange = dateRangeRef.current.getResult() || {};
					const minTimes = minTimesRef.current.getResult() || {};
					return {
						...dateRange,
						...minTimes,
					};
				},
			};
		},
		[]
	);

	return (
		<React.Fragment>
			<Grid item xs={4}>
				<DateIntervalField
					ref={dateRangeRef}
					label="時間區間"
					startDateProps={{
						defaultValue: paramsData.startDate || getFirstOfMonth(getToday()),
					}}
					endDateProps={{ defaultValue: paramsData.endDate || getToday() }}
					fullWidth
				/>
			</Grid>
			<Grid item xs={2}>
				<CrossClubMinTimesField ref={minTimesRef} paramsData={paramsData} label="單店跨店次數以上" fullWidth />
			</Grid>
		</React.Fragment>
	);
});

const crossClubMemberSheet = (isRevealed, isVisitList) => ({
	...getMemberSheet({ isRevealed }),
	headerLabel: isVisitList ? "他店來店會員" : "會員",
	width: 18,
	align: "left",
	formatCell: (_value, rowData, other) => {
		const { displayName, nickName, isLocalClubMember } = rowData;
		if (!isLocalClubMember) return <Typography>{nickName || displayName || "-"}</Typography>;
		return getMemberSheet({ isRevealed }).formatCell(_value, rowData, other);
	},
});

const crossClubCheckboxSheet = {
	...rowLabelCheckboxSheet,
	formatCell: (value, rowData, other) => {
		if (!rowData.isLocalClubMember) return null;
		return rowLabelCheckboxSheet.formatCell(value, rowData, other);
	},
};

const crossClubCellPhoneSheet = isRevealed => ({
	cellKey: "cellPhone",
	headerLabel: "手機",
	width: 13,
	align: "center",
	formatCell: (cellPhone, { maskCellPhone, isLocalClubMember }) => {
		if (!isLocalClubMember) return "-";
		return <PrivacyDataToggle fullData={cellPhone} maskedData={maskCellPhone} isRevealed={isRevealed} />;
	},
});

const crossClubSpecialConditionSheet = {
	cellKey: "specialCondition",
	headerLabel: "聯繫注意與偏好",
	width: 18,
	formatCell: (specialCondition, { memberID, isLocalClubMember }, { setSourceDataById }) => {
		if (!isLocalClubMember) return "-";
		return <SpecialConditionBox sourceData={{ memberID, value: specialCondition }} setSourceDataById={setSourceDataById} />;
	},
};

const crossClubContactSheet = {
	...contactSheet,
	formatCell: (value, rowData, other) => {
		if (!rowData.isLocalClubMember) return "-";
		return contactSheet.formatCell(value, rowData, other);
	},
};

const crossClubMoreSheet = {
	...moreSheet,
	formatCell: (value, rowData, other) => {
		if (!rowData.isLocalClubMember) return null;
		return moreSheet.formatCell(value, rowData, other);
	},
};

const CrossClubWorkoutCell = ({ nickName, originalClubName, crossClubName, quantity, workoutDates, dialogRef }) => {
	const handleOpenDialog = () => {
		dialogRef?.current?.openCrossClubWorkoutDatesDialog?.({
			nickName,
			originalClubName,
			crossClubName,
			crossClubWorkoutQuantity: quantity || 0,
			workoutDates,
		});
	};

	return (
		<React.Fragment>
			<Tooltip title="查看跨店運動日期">
				<Button variant="text" onClick={handleOpenDialog}>
					<span>
						{crossClubName || "-"}
						<br />
						{quantity || 0}
					</span>
				</Button>
			</Tooltip>
		</React.Fragment>
	);
};

const originalClubWorkoutSummarySheet = {
	cellKey: "originalClubWorkoutQuantity",
	headerLabel: "原店運動次數",
	width: 16,
	align: "center",
	formatCell: (quantity, { originalClubName }) => (
		<Typography>
			{originalClubName || "-"}
			<br />
			{quantity || 0}
		</Typography>
	),
};

const originalClubNameSheet = {
	cellKey: "originalClubName",
	headerLabel: "原店",
	width: 14,
	align: "center",
};

const crossClubWorkoutSummarySheet = {
	cellKey: "crossClubWorkoutQuantity",
	headerLabel: "跨店運動次數",
	width: 18,
	align: "center",
	formatCell: (quantity, { nickName, displayName, originalClubName, crossClubName, crossClubWorkoutDates }, { dialogRef }) => (
		<CrossClubWorkoutCell
			nickName={nickName || displayName}
			originalClubName={originalClubName}
			crossClubName={crossClubName}
			quantity={quantity}
			workoutDates={crossClubWorkoutDates}
			dialogRef={dialogRef}
		/>
	),
};

export const getFilterCrossClubLabel = (isRevealed, isVisitList = false) => {
	const labels = [
		{ ...crossClubMemberSheet(isRevealed, isVisitList), isFixed: true, isFixedLeft: true },
		originalClubWorkoutSummarySheet,
		crossClubWorkoutSummarySheet,
	];

	if (isVisitList) return [labels[0], originalClubNameSheet, labels[2]];

	return [
		crossClubCheckboxSheet,
		labels[0],
		crossClubCellPhoneSheet(isRevealed),
		crossClubSpecialConditionSheet,
		...labels.slice(1),
		crossClubContactSheet,
		crossClubMoreSheet,
	];
};
